/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView, Text } from '@components/atoms'
import { Grid, boxColumns, productPackagingColumns } from '@grid'
import {
	useAppSelector,
	useAppDispatch,
	boxActions,
	ApiGetDataStatus,
} from '@redux/index'
import { BoxType, ProductPackagingType } from '@schemas'
import { GridRowModel } from '@mui/x-data-grid'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'

/**
 * 박스 관리 (SHIPPING_PLAN.md §2.1).
 * 위: 박스 규격(인라인 CRUD). 아래: 제품 ↔ 박스 연결과 제품 무게.
 * 라벨 무게 = 빈 박스 + Σ(제품 무게 × 수량).
 */
const BoxPage = () => {
	const reduxDispatch = useAppDispatch()

	const boxList: BoxType[] = useAppSelector(state => state.box.boxList)
	const boxStatus: ApiGetDataStatus = useAppSelector(state => state.box.boxListStatus)
	const packagingList: ProductPackagingType[] = useAppSelector(
		state => state.box.packagingList,
	)
	const packagingStatus: ApiGetDataStatus = useAppSelector(
		state => state.box.packagingListStatus,
	)
	const error: string = useAppSelector(state => state.box.error)

	const packagingColumns = React.useMemo(
		() => productPackagingColumns(boxList),
		[boxList],
	)

	const onBoxAction = (iudFlag: string, aRow: GridRowModel) => {
		reduxDispatch(boxActions.ERROR(''))
		reduxDispatch(
			boxActions.UPDATE_BOX({
				iudFlag: iudFlag as 'I' | 'U' | 'D',
				boxId: aRow.id,
				code: aRow.code ?? '',
				name: aRow.name ?? '',
				widthCm: Number(aRow.widthCm ?? 0),
				heightCm: Number(aRow.heightCm ?? 0),
				depthCm: Number(aRow.depthCm ?? 0),
				tareKg: Number(aRow.tareKg ?? 0),
				useYn: Number(aRow.useYn ?? 1),
				memo: aRow.memo ?? '',
			}),
		)
	}

	const onPackagingAction = (iudFlag: string, aRow: GridRowModel) => {
		if (iudFlag !== 'U') {
			// 제품 행은 여기서 만들거나 지우지 않는다 — 목록만 다시 읽어 되돌린다
			reduxDispatch(boxActions.TRIGGER_INIT_BOX_PAGE(true))
			return
		}
		reduxDispatch(
			boxActions.UPDATE_PACKAGING({
				productId: aRow.id,
				boxId: aRow.boxId ?? '',
				netKg:
					aRow.netKg === null || aRow.netKg === undefined || aRow.netKg === ''
						? null
						: Number(aRow.netKg),
			}),
		)
	}

	return (
		<View css={style}>
			<BoxView title="박스 규격" className="boxBand">
				<View className="bandBody">
					{error !== '' && <Text className="boError">{error}</Text>}
					<Grid
						title="박스 규격"
						rows={boxList}
						columns={boxColumns}
						loading={boxStatus === 'loading'}
						onAction={onBoxAction}
						renderCell={{}}
						renderEditCell={{}}
					/>
				</View>
			</BoxView>

			<BoxView title="제품 포장" className="boxBand">
				<View className="bandBody">
					<Text className="hint">
						제품마다 들어가는 박스와 제품 무게(kg)를 둔다. 라벨을 만들 때 박스·치수·무게의
						기본값이 된다. 여러 대 주문은 체크아웃이 정한 박스(A~E)를 쓴다.
					</Text>
					<Grid
						title="제품 포장"
						rows={packagingList}
						columns={packagingColumns}
						loading={packagingStatus === 'loading'}
						onAction={onPackagingAction}
						renderCell={{}}
						renderEditCell={{}}
					/>
				</View>
			</BoxView>
		</View>
	)
}

export default BoxPage

const style = css({
	...boShared,
	display: 'flex',
	flexDirection: 'column',
	gap: 24,
	'.boxBand': { minHeight: 320 },
	'.bandBody': { display: 'flex', flexDirection: 'column', width: '100%' },
	'.hint': { fontSize: 12, color: mono.dim, margin: '0 0 8px' },
	'.boError': { margin: '0 0 8px' },
})
